import { useCallback, useEffect, useState } from "react";
import { mutes as mutesApi } from "../api/client";

/**
 * Which aircraft types are currently silenced, and a one-tap way to change that.
 *
 * Lifted to the map page rather than fetched inside the detail sheet: the sheet mounts on every
 * selection, and re-fetching the mute list each time a marker is tapped would be a request per
 * tap for data that changes once a week.
 */
export function useMutes() {
  const [muted, setMuted] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const list = await mutesApi.list();
      setMuted(new Set(list.map((m) => m.icao_type)));
    } catch {
      // An unreadable mute list must not break the map. The cost of being wrong here is one
      // notification too many, not a broken page.
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const toggle = useCallback(
    async (icaoType: string) => {
      setBusy(true);
      // Optimistic: this is a one-tap control on a bottom sheet, and a round trip's worth of
      // nothing-happening reads as a dead button.
      const wasMuted = muted.has(icaoType);
      setMuted((current) => {
        const next = new Set(current);
        if (wasMuted) next.delete(icaoType);
        else next.add(icaoType);
        return next;
      });
      try {
        if (wasMuted) await mutesApi.remove(icaoType);
        else await mutesApi.add(icaoType);
      } catch {
        await reload(); // put it back the way the server sees it
      } finally {
        setBusy(false);
      }
    },
    [muted, reload],
  );

  return { muted, toggle, busy, reload };
}
